import * as React from 'react';
import { useState } from 'react';
import LanguageService from '../../../shared/LanguageService';
import { hasOnlyAllowedHtml } from '../../../shared/allowedHtml';
import { readApiErrorMessage } from '../../../shared/api/readApiErrorMessage';
import { useUpdateTranslationMutation } from '../queries/useUpdateTranslationMutation';
import { placeholdersMatch } from './placeholders';
import { previewTranslation } from './translationPreview';
import {
    TranslationDto,
    TranslationLanguage,
    translationLanguages,
    UpdateTranslationInput
} from '../types';

type FormValues = Record<TranslationLanguage, string>;

function initialValues(translation: TranslationDto): FormValues {
    return {
        sv: translation.translations.sv || '',
        en: translation.translations.en || '',
        fi: translation.translations.fi || ''
    };
}

function loadedLanguageKey(): string | null {
    const loadedLanguage = LanguageService.getLanguage();
    return loadedLanguage ? loadedLanguage.key : null;
}

export function useTranslationForm(
    translation: TranslationDto,
    onSaved: (saved: TranslationDto) => void,
    onFailed: (message: string) => void
) {
    const [values, setValues] = useState<FormValues>(initialValues(translation));
    const update = useUpdateTranslationMutation();

    const changedLanguages = translationLanguages.filter(
        (language) => values[language] !== (translation.translations[language] || '')
    );
    const blankLanguages = changedLanguages.filter((language) => !values[language].trim());
    const placeholderErrorLanguages = changedLanguages.filter(
        (language) => !placeholdersMatch(translation.translations[language], values[language])
    );
    const htmlErrorLanguages = changedLanguages.filter(
        (language) => !hasOnlyAllowedHtml(values[language])
    );
    const canSave =
        changedLanguages.length > 0 &&
        blankLanguages.length === 0 &&
        placeholderErrorLanguages.length === 0 &&
        htmlErrorLanguages.length === 0 &&
        !update.isLoading;

    function hasPlaceholderError(language: TranslationLanguage): boolean {
        return placeholderErrorLanguages.indexOf(language) !== -1;
    }

    function hasHtmlError(language: TranslationLanguage): boolean {
        return htmlErrorLanguages.indexOf(language) !== -1;
    }

    function errorFor(language: TranslationLanguage): string | null {
        if (hasHtmlError(language)) {
            return 'translation-editor/error/html-not-allowed';
        }
        if (hasPlaceholderError(language)) {
            return 'translation-editor/error/placeholders-changed';
        }
        return null;
    }

    function changeValue(language: TranslationLanguage, text: string) {
        setValues({ ...values, [language]: text });
        if (language === loadedLanguageKey()) {
            previewTranslation(translation.key, text);
        }
    }

    async function save(event: React.FormEvent) {
        event.preventDefault();
        const input: UpdateTranslationInput = { translations: {} };
        for (const language of changedLanguages) {
            input.translations[language] = values[language];
        }

        try {
            const saved = await update.mutateAsync({ key: translation.key, input });
            onSaved(saved);
        } catch (error) {
            onFailed(await readApiErrorMessage(error));
        }
    }

    return { values, canSave, errorFor, changeValue, save };
}
